import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { X } from "lucide-react"
import { toast } from "sonner"
import trialLogo from '@/assets/images/idata-logo.gif'
import { userApi } from '@/http/apiList'
import { rsaEncrypt, pubKey } from "@/utils/encrypt"
import { useAuth } from "@/context/AuthContext"

export default function LoginModal({ open, onOpenChange, onForgotPasswordClick }) {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  const navigate = useNavigate()
  const { refreshAuthStatus } = useAuth();

  // Basic email validation regex
  const validateEmail = (email) => {
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)
  }

  const handleValidation = () => {
    const newErrors = {}
    if (!email) {
      newErrors.email = "Corporate Email is required."
    } else if (!validateEmail(email)) {
      newErrors.email = "Please enter a valid email address."
    }

    if (!password) {
      newErrors.password = "Password is required."
    } 

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!handleValidation()) {
      return
    }

    setIsLoading(true)
    setErrors({})
    try {
      const data = await userApi.login({
        email: btoa(rsaEncrypt(email, pubKey)),
        password: btoa(rsaEncrypt(password, pubKey))
      });
      if (data.code === 1) {
        toast.success('success');
        onOpenChange(false);
        // Reset form
        setEmail("")
        setPassword("")
        await refreshAuthStatus()
        const from = location.state?.from?.pathname || "/data-product"
        navigate(from, { replace: true })
      } else {
        // Handle login failure (e.g., incorrect credentials)
        setErrors({ api: data.subMsg || 'error' })
      }
    } catch (error) {
      // Handle network or other unexpected errors
      setErrors({ api: error.response.data || 'error' })
    } finally {
      setIsLoading(false)
    }
  }

  const handleOpenChange = (isOpen) => {
    onOpenChange(isOpen)
    if (!isOpen) {
      // Reset state when modal is closed
      setEmail("")
      setPassword("")
      setErrors({})
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[442px] px-[44px] py-[32px] bg-white">
        <form onSubmit={handleSubmit}>
          <DialogHeader className="items-center text-center">
            <img
              src={trialLogo}
              alt="iData Logo"
              className="h-[160px] h-[50px] w-auto mb-4"
            />
            <DialogTitle className="text-[30px] font-bold text-[#0066ff] mt-[0px]">Login</DialogTitle>
          </DialogHeader>
          <div className="grid pt-[20px]">
            <div className="grid gap-2 mb-[34px]">
              <Label htmlFor="email" className="text-[16px] text-[#161616] font-bold">Corporate Email</Label>
              <Input
                id="email"
                placeholder="corporate Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`placeholder:text-[rgba(77,77,77,0.40)] ${errors.email ? "border-red-500" : ""}`}
              />
              {errors.email && <p className="text-sm text-red-500">{errors.email}</p>}
            </div>
            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-[16px] text-[#161616] font-bold">Password</Label>
                <Button
                  type="button"
                  variant="link"
                  className="text-[16px] text-[#4790fd] h-auto p-0"
                  onClick={onForgotPasswordClick}
                >
                  Forgot Password
                </Button>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`placeholder:text-[rgba(77,77,77,0.40)] ${errors.email ? "border-red-500" : ""}`}
              />
              {errors.password && <p className="text-sm text-red-500">{errors.password}</p>}
            </div>
            {errors.api && <p className="text-[16px] text-[#ED2D00] leading-[24px]">{errors.api}</p>}
          </div>
          <p className="text-[16px] text-[#4D4D4D] mt-[24px] mb-[20px]">
            This is a secure iData authentication service that allows you access to iData services from wherever you are.
          </p>
          <DialogFooter className="mt-4">
            <Button type="submit" disabled={isLoading} className="w-full bg-[#0066ff] hover:bg-[#297FFF] text-[24px] text-white py-6">
              {isLoading ? "Submitting..." : "Submit"}
            </Button>
          </DialogFooter>
        </form>
        <DialogClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogClose>
      </DialogContent>
    </Dialog>
  )
}